import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/Modal';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { ListPagination } from '@/components/ListPagination';
import { usePreferenceScope } from '@/hooks/useStoredPreference';
import { api } from '@/lib/api';
import { deadlineLabel } from '@/pages/Opportunities/dates';
import type { OpportunityEvent } from '@/pages/Opportunities/types';

interface PlanningHistoryPage { data: OpportunityEvent[]; total: number; actors: { id: number; name: string }[]; }
export interface PlanningHistoryProps { onClose: () => void; }
export const PlanningHistory = ({ onClose }: PlanningHistoryProps) => {
    const { t, i18n } = useTranslation('global');
    const scope = usePreferenceScope();
    const [page, setPage] = useState(1);
    const query = useQuery({ queryKey: ['opportunities', scope, 'planning-history', page], retry: false,
        queryFn: async ({ signal }) => (await api.get<{ data: PlanningHistoryPage }>('/opportunities/planning-history', { params: { page, per_page: 20 }, signal })).data.data });
    return <Modal isOpen title={t('opportunities.planning_history')} onClose={onClose} hideFooter maxWidth="2xl">
        <div className="space-y-4 text-slate-900 dark:text-zinc-100">
            {query.isFetching && <p role="status">{t('opportunities.loading')}</p>}
            {query.isError && <Alert variant="error"><Button variant="outline" onClick={() => { void query.refetch(); }}>{t('opportunities.retry')}</Button></Alert>}
            {query.data?.data.map(event => <article key={event.id} className="space-y-2 rounded-xl border border-slate-200 p-4 dark:border-white/15">
                <p className="font-semibold">{t(`opportunities.event.${event.action}`)} · {query.data.actors.find(actor => actor.id === event.actor_id)?.name ?? t('opportunities.system')}</p>
                <time>{deadlineLabel(event.created_at, i18n.language)}</time>
                <details><summary className="cursor-pointer">{t('opportunities.view_changes')}</summary><pre className="max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs">{JSON.stringify(event.changes, null, 2)}</pre></details>
            </article>)}
            {query.data?.total === 0 && <p>{t('opportunities.no_events')}</p>}
            <ListPagination page={page} pageSize={20} total={query.data?.total ?? 0} onPage={setPage} />
        </div>
    </Modal>;
};
